import { useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { AlertTriangle, Loader2, Play } from "lucide-react";

import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useGetStoresOptionsQuery } from "@/services/analyst-assignment/select-options";
import { useGetOfmAvailabilityQuery } from "@/services/tax-closing";
import { useGetMonthControlsQuery } from "@/services/tax-closing-period";

import { StoreMultiSelect } from "./store-multi-select";
import { buildExportMonthOptions } from "./month-options";

function getYearOptions(): number[] {
  const current = new Date().getFullYear();
  // 6 anos retroativos — necessário pra comparações históricas.
  return Array.from({ length: 6 }, (_, i) => current - i);
}

interface FiltersSidebarProps {
  onGenerate: (stores: number[], month: number, year: number) => void;
  isDisabled: boolean;
  isLoading: boolean;
}

interface FiltersFormValues {
  year: number;
  month: number;
  selectedStores: number[];
}

// --------------------------------------------------------------------
// FiltersSidebar — compact left rail with the form fields and the
// primary "Gerar" action. No Card wrapper, no centralization, dense
// vertical stack.
// --------------------------------------------------------------------
export function FiltersSidebar({
  onGenerate,
  isDisabled,
  isLoading,
}: FiltersSidebarProps) {
  const now = new Date();
  const form = useForm<FiltersFormValues>({
    defaultValues: {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      selectedStores: [],
    },
  });

  const { control, handleSubmit } = form;
  const year = useWatch({ control, name: "year" });
  const month = useWatch({ control, name: "month" });
  const selectedStores = useWatch({ control, name: "selectedStores" });

  const { data: storeOptions = [], isLoading: isLoadingStores } =
    useGetStoresOptionsQuery();

  const { data: availability } = useGetOfmAvailabilityQuery(
    { year, month },
    { skip: !year || !month },
  );
  const availableSet = useMemo(
    () => new Set(availability?.lojasDisponiveis ?? []),
    [availability],
  );
  const pendingStores = useMemo(
    () => availability ? selectedStores.filter((code) => !availableSet.has(code)) : [],
    [availability, availableSet, selectedStores],
  );
  const availableSelectedCount = selectedStores.length - pendingStores.length;

  // Períodos cadastrados (LIBERADO/BLOQUEADO/FECHADO) pra gating do dropdown.
  const { data: periods = [] } = useGetMonthControlsQuery();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Opções de mês: oculta meses futuros (Cenário 6) e desabilita meses
  // BLOQUEADO/FECHADO/sem cadastro com tooltip explicativo (Cenário 5).
  // Só meses LIBERADO são selecionáveis. Lógica isolada em ./month-options.
  const monthOptions = useMemo(
    () => buildExportMonthOptions(periods, year, currentYear, currentMonth),
    [periods, year, currentYear, currentMonth],
  );

  const selectedMonthBlocked = useMemo(
    () => monthOptions.find((o) => o.m === month)?.isBlocked ?? false,
    [monthOptions, month],
  );

  // Mês bloqueado no Controle de Meses Fiscais não permite seleção de lojas —
  // independente de haver (ou não) dados OFM no Redis. Limpa qualquer seleção
  // remanescente ao bloquear.
  useEffect(() => {
    if (selectedMonthBlocked && selectedStores.length > 0) {
      form.setValue("selectedStores", []);
    }
  }, [selectedMonthBlocked, selectedStores.length, form]);

  const canGenerate =
    !isDisabled && !isLoading && selectedStores.length > 0 && !selectedMonthBlocked;

  // When a job is running (or being created) the form must be locked to
  // prevent the user from accidentally changing the parameters that the
  // current extraction is using.
  const isLocked = isDisabled || isLoading;

  const handleGenerate = () => {
    void handleSubmit(({ selectedStores, month, year }) => {
      if (canGenerate) onGenerate(selectedStores, month, year);
    })();
  };

  return (
    <aside className="h-full border rounded-lg bg-background p-5 flex flex-col gap-5">
      <header>
        <h2 className="text-sm font-semibold text-foreground">Parâmetros</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Defina o período e as lojas
        </p>
      </header>

      <div className="flex flex-col gap-4">
        <Field label="Ano">
          <Controller
            control={control}
            name="year"
            render={({ field }) => (
              <Select
                value={String(field.value)}
                onValueChange={(v) => field.onChange(Number(v))}
                disabled={isLocked}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getYearOptions().map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <Field label="Mês">
          <Controller
            control={control}
            name="month"
            render={({ field }) => (
              <Select
                value={String(field.value)}
                onValueChange={(v) => field.onChange(Number(v))}
                disabled={isLocked}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((o) =>
                    o.isBlocked ? (
                      <SelectItem
                        key={o.m}
                        value={String(o.m)}
                        disabled
                        title={o.reason ?? undefined}
                        className="!pointer-events-auto cursor-not-allowed"
                      >
                        {o.name}
                      </SelectItem>
                    ) : (
                      <SelectItem key={o.m} value={String(o.m)}>
                        {o.name}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <Field
          label="Lojas"
          hint={
            selectedStores.length > 0
              ? availability
                ? `${selectedStores.length} selecionada(s) — ${availableSelectedCount} disponíveis, ${pendingStores.length} pendentes`
                : `${selectedStores.length} ${selectedStores.length === 1 ? "selecionada" : "selecionadas"}`
              : undefined
          }
        >
          <Controller
            control={control}
            name="selectedStores"
            render={({ field }) => (
              <StoreMultiSelect
                options={storeOptions}
                selected={field.value}
                onChange={field.onChange}
                isLoading={isLoadingStores}
                disabled={isLocked || selectedMonthBlocked}
                availableSet={availability ? availableSet : undefined}
              />
            )}
          />
        </Field>

        {selectedMonthBlocked && (
          <div className="text-xs rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2">
            O mês selecionado não está liberado no Controle de Meses Fiscais.
            Seleção de lojas e exportação indisponíveis para este período.
          </div>
        )}

        {availability && availability.lojasDisponiveis.length === 0 && (
          <div className="text-xs rounded border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2">
            Nenhum dado OFM disponível para {String(month).padStart(2, "0")}/{year}.
          </div>
        )}

        {availability && pendingStores.length > 0 && (
          <div className="text-xs rounded border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>{pendingStores.length}</strong> loja(s) sem dados OFM para o período.
              O reprocessamento pode ser solicitado na tela de detalhes da extração.
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto pt-4 flex justify-end">
        <Button
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="w-full gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Processando
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Gerar extração
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-medium text-foreground">{label}</label>
        {hint && (
          <span className="text-[10px] text-muted-foreground">{hint}</span>
        )}
      </div>
      {children}
    </div>
  );
}
